<script setup lang="ts">
  import { computed, onBeforeUnmount, ref } from 'vue';
  import PlusIcon from '@/shared/assets/icons/PlusIcon.svg';
  import { IconImage } from '@/shared/ui';

  interface Props {
    direction: 'row' | 'column';
    disabled?: boolean;
    segment?: { top: number; height: number } | null;
  }

  const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    segment: null,
  });

  const emit = defineEmits<(e: 'add') => void>();
  const isPressed = ref(false);
  let feedbackTimeout: ReturnType<typeof setTimeout> | null = null;

  // A broken table renders one column button per chunk; `segment` bounds each button to
  // its chunk's vertical band instead of spanning the whole (cross-page) wrapper height.
  const isSegmentedColumn = computed(() => props.direction === 'column' && !!props.segment);

  const positionClasses = computed(() => {
    if (props.direction !== 'column') {
      return '-bottom-[26px] left-0 w-full h-6';
    }
    return isSegmentedColumn.value ? '-right-[26px] w-6' : 'top-0 -right-[26px] w-6 h-full';
  });

  const segmentStyle = computed(() =>
    isSegmentedColumn.value
      ? { top: `${props.segment!.top}px`, height: `${props.segment!.height}px` }
      : undefined
  );

  const stateClasses = computed(() => {
    if (props.disabled) {
      return 'cursor-not-allowed text-[var(--color-gray-02)]';
    }

    return [
      'cursor-pointer text-[var(--color-gray-03)]',
      'hover:bg-[var(--color-gray-02)]/70 active:bg-[var(--color-gray-02)]/80',
      isPressed.value ? 'opacity-60 bg-[var(--color-gray-02)]/80' : '',
    ];
  });

  const handleClick = () => {
    if (props.disabled) {
      return;
    }

    emit('add');
    isPressed.value = true;

    if (feedbackTimeout) {
      clearTimeout(feedbackTimeout);
    }

    feedbackTimeout = setTimeout(() => {
      isPressed.value = false;
      feedbackTimeout = null;
    }, 220);
  };

  onBeforeUnmount(() => {
    if (feedbackTimeout) {
      clearTimeout(feedbackTimeout);
    }
  });
</script>

<template>
  <button
    v-tippy="{
      content: direction === 'row' ? 'Adicionar linha' : 'Adicionar coluna',
      placement: 'top',
      theme: 'custom',
      trigger: 'mouseenter',
      hideOnClick: true,
    }"
    type="button"
    class="table-add-button absolute z-20 flex items-center justify-center rounded-lg border border-[var(--color-border-color)] bg-[var(--color-gray-02)]/50 p-0 opacity-0 transition-[opacity,background-color,border-color] duration-150 ease-[ease] group-hover:opacity-100 group-[.is-selected]:opacity-100"
    :class="[direction, positionClasses, stateClasses]"
    :style="segmentStyle"
    :disabled="disabled"
    :aria-label="direction === 'row' ? 'Adicionar linha' : 'Adicionar coluna'"
    @click.stop="handleClick"
  >
    <IconImage
      :src="PlusIcon"
      alt=""
    />
  </button>
</template>
